import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Autocomplete,
    Chip,
    CloseButton,
    EmptyState,
    Header,
    Label,
    ListBox,
    SearchField,
    Separator,
    Tag,
    TagGroup,
    useFilter,
} from '@heroui/react';
import type { ChartAccount } from '@/api/models';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    accountFilterSections,
    staleFilterCodes,
} from './accountFilterPickerMeta';

type AccountFilterPickerProps = {
    /** testid discriminator — `${pairGroup}-${side}`, appended after the literal slot. */
    testId: string;
    /** The full chart — group headers and postable leaves, every class. */
    accounts: ChartAccount[];
    value: string[];
    onChange: (codes: string[]) => void;
    /** Told when this picker's own popover opens or closes. */
    onOpenChange?: (open: boolean) => void;
};

const CAPTION = 'text-[9px] font-bold uppercase tracking-widest text-muted';

/**
 * The slot's "which accounts" picker: multi-select, searchable, sectioned by
 * account class — sourced from the live Chart of Accounts instead of typed
 * from memory.
 *
 * **Keyed by account code**, the same string `accountFilter` already stores
 * on the wire, so picking replaces typing without any format change
 * underneath it. Selecting a group ("11 — Non-current tangible assets")
 * stores that group's own code, which the server's `code.startsWith(prefix)`
 * matching already treats as a prefix — identical to typing "11" today.
 *
 * A code already in `value` that the picker doesn't recognize (archived,
 * renamed, or from a chart that has since changed) is shown as a separate
 * muted, individually-removable chip below the picker rather than silently
 * dropped — the picker itself only ever manages the codes it can offer.
 */
export const AccountFilterPicker: React.FC<AccountFilterPickerProps> = ({
    testId,
    accounts,
    value,
    onChange,
    onOpenChange,
}) => {
    const { t } = useTranslation('pages');
    const { contains } = useFilter({ sensitivity: 'base' });

    const sections = React.useMemo(
        () => accountFilterSections(accounts),
        [accounts],
    );
    const stale = React.useMemo(
        () => staleFilterCodes(value, accounts),
        [value, accounts],
    );
    const known = React.useMemo(
        () => value.filter((code) => !stale.includes(code)),
        [value, stale],
    );

    const removeStale = (code: string) =>
        onChange(value.filter((entry) => entry !== code));

    return (
        <div
            className="flex flex-col gap-1.5"
            {...getNodeTestIdProp(`operation-editor-filter-${testId}`)}
        >
            <Autocomplete
                onChange={(keys) => onChange([...stale, ...(keys as string[])])}
                onOpenChange={onOpenChange}
                selectionMode="multiple"
                value={known}
            >
                <Label className={CAPTION}>
                    {t('operations.editor.filterLabel')}
                </Label>
                <Autocomplete.Trigger className="min-h-0 py-1.5 text-xs">
                    <Autocomplete.Value>
                        {({ defaultChildren, isPlaceholder }) => {
                            if (isPlaceholder || known.length === 0) {
                                return defaultChildren;
                            }
                            return (
                                <TagGroup
                                    onRemove={(keys) =>
                                        onChange(
                                            value.filter(
                                                (code) => !keys.has(code),
                                            ),
                                        )
                                    }
                                    size="sm"
                                >
                                    <TagGroup.List>
                                        {known.map((code) => (
                                            <Tag id={code} key={code}>
                                                {code}
                                            </Tag>
                                        ))}
                                    </TagGroup.List>
                                </TagGroup>
                            );
                        }}
                    </Autocomplete.Value>
                    <Autocomplete.ClearButton />
                    <Autocomplete.Indicator />
                </Autocomplete.Trigger>
                <Autocomplete.Popover>
                    <Autocomplete.Filter filter={contains}>
                        <SearchField
                            autoFocus
                            name={`account-filter-${testId}`}
                        >
                            <SearchField.Group>
                                <SearchField.SearchIcon />
                                <SearchField.Input
                                    placeholder={t(
                                        'operations.editor.filterPlaceholder',
                                    )}
                                />
                                <SearchField.ClearButton />
                            </SearchField.Group>
                        </SearchField>
                        <ListBox
                            renderEmptyState={() => (
                                <EmptyState>
                                    {t('operations.editor.filterNoResults')}
                                </EmptyState>
                            )}
                        >
                            {sections.map((section, index) => (
                                <React.Fragment key={section.id}>
                                    {index > 0 ? <Separator /> : null}
                                    <ListBox.Section>
                                        <Header>{section.label}</Header>
                                        {section.options.map((option) => (
                                            <ListBox.Item
                                                id={option.code}
                                                key={option.code}
                                                textValue={option.searchText}
                                            >
                                                <span className="flex min-w-0 items-center gap-2">
                                                    <span className="shrink-0 text-xs tabular-nums text-muted">
                                                        {option.code}
                                                    </span>
                                                    <span className="truncate">
                                                        {option.name}
                                                    </span>
                                                </span>
                                                <ListBox.ItemIndicator />
                                            </ListBox.Item>
                                        ))}
                                    </ListBox.Section>
                                </React.Fragment>
                            ))}
                        </ListBox>
                    </Autocomplete.Filter>
                </Autocomplete.Popover>
            </Autocomplete>

            {stale.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                    {stale.map((code) => (
                        <Chip
                            key={code}
                            size="sm"
                            variant="soft"
                            {...getNodeTestIdProp(
                                `operation-editor-filter-stale-${testId}-${code}`,
                            )}
                        >
                            {code}
                            <CloseButton
                                aria-label={t(
                                    'operations.editor.filterRemoveStale',
                                    { code },
                                )}
                                onPress={() => removeStale(code)}
                            />
                        </Chip>
                    ))}
                </div>
            ) : null}
        </div>
    );
};
